import { ApolloError, useMutation } from "@apollo/client";
import { KIND } from "baseui/button";
import { ConfirmDialog } from "components/confirm-dialog";
import { Content } from "components/content";
import { FormRenderer } from "components/form-renderer";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { useAuth } from "contexts/auth-context";
import { useLoading } from "contexts/loading-context";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { DeviceFloppy, FileOff, Note } from "tabler-icons-react";
import { PERMISSIONS } from "utils/permissions";
import { scrollOnError } from "utils/scrollOnError";

import { PROJECTS_FIELDS, ProjectsFormInputs } from "../projects.form";
import { PROJECTS_CREATE } from "../projects.gql";

export default function ProjectsCreate(): React.ReactElement {
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] = useState(
    false
  );
  const [isClearConfirmDialogOpen, setIsClearConfirmDialogOpen] = useState(
    false
  );
  const [errorTimeStamp, setErrorTimeStamp] = useState<number>();
  const { isLoading, setIsLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();
  const { checkPermission } = useAuth();
  const history = useHistory();

  const {
    control,
    formState: { errors, isDirty, ...formState },
    handleSubmit,
    reset,
    setValue,
    watch,
    ...methods
  } = useForm<ProjectsFormInputs>({
    defaultValues: {
      name: "",
      isActive: true,
      allowedDomains: "",
      languages: [],
      mainLanguage: [],
      metaTitle: "",
      metaDescription: "",
      metaKeywords: "",
      ogTitle: "",
      ogDescription: "",
      robots: [],
      canonical: "",
    },
  });

  const [createProject, { error }] = useMutation(PROJECTS_CREATE);

  const onError = (errors: Record<string, unknown>, e: any) =>
    setErrorTimeStamp(e.timeStamp);

  useEffect(() => {
    if (errors) scrollOnError(errors, PROJECTS_FIELDS, "create");
  }, [errorTimeStamp]);

  const onSubmit = async ({
    isActive,
    allowedDomains,
    languages,
    mainLanguage,
    metaTitle,
    metaDescription,
    metaKeywords,
    ogTitle,
    ogDescription,
    canonical,
    robots,
    ...values
  }: ProjectsFormInputs): Promise<void> => {
    setIsLoading(true);

    try {
      const { data } = await createProject({
        variables: {
          projectCreateInput: {
            isActive: !!isActive,
            ...(!!allowedDomains && {
              allowedDomains: allowedDomains
                ?.split(/\r?\n/)
                .filter((line) => line.trim() !== "")
                .join("\n")
                .trim()
                .split("\n"),
            }),
            languages: languages?.map(
              ({ id }: { id: string | number }) => id as string
            ),
            mainLanguage: mainLanguage?.[0]?.id,
            ...values,
          },
          seoTagsetUpsertInput: {
            metaTitle,
            metaDescription,
            metaKeywords,
            ogTitle,
            ogDescription,
            canonical,
            robots: robots?.length ? robots?.[0]?.label : null,
          },
        },
      });

      enqueueSnackbar({
        message: "Utworzono pomyślnie",
        variant: "success",
      });
      history.push(`/projects/${data?.projectCreate?.id}`);
    } catch (error: unknown) {
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const watchLanguages = watch("languages");
  const watchMainLanguage = watch("mainLanguage");

  useEffect(() => {
    if (
      !watchLanguages?.length ||
      !watchLanguages?.some((item) => item.id === watchMainLanguage?.[0]?.id)
    )
      setValue("mainLanguage", []);
  }, [watchLanguages]);

  if (!checkPermission(PERMISSIONS.project.create))
    return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title="Nowy projekt"
        labels={["Projekty", "Tworzenie"]}
        goBackOption
        buttons={[
          {
            label: "Wyczyść",
            kind: KIND.secondary,
            startEnhancer: <Note size={18} />,
            disabled: isLoading,
            onClick: () => {
              setIsClearConfirmDialogOpen(true);
            },
          },
          {
            label: "Anuluj",
            kind: KIND.secondary,
            startEnhancer: <FileOff size={18} />,
            disabled: isLoading,
            onClick: () =>
              isDirty ? setIsCancelConfirmDialogOpen(true) : history.goBack(),
          },
          {
            label: "Utwórz",
            kind: KIND.primary,
            startEnhancer: <DeviceFloppy size={18} />,
            onClick: handleSubmit(onSubmit, onError),
            disabled: isLoading,
            isLoading: isLoading,
            type: "submit",
            formId: "createProject",
          },
        ]}
      />
      <Content>
        <FormProvider<ProjectsFormInputs>
          control={control}
          formState={{ errors, isDirty, ...formState }}
          handleSubmit={handleSubmit}
          reset={reset}
          watch={watch}
          setValue={setValue}
          {...methods}
        >
          <FormRenderer<ProjectsFormInputs>
            id="createProject"
            onSubmit={handleSubmit(onSubmit)}
            type="create"
            isLoading={isLoading}
            error={error}
            fields={PROJECTS_FIELDS}
          />
        </FormProvider>
        <ConfirmDialog
          isOpen={isCancelConfirmDialogOpen}
          label="Anulowanie tworzenia nowego projektu"
          close={() => setIsCancelConfirmDialogOpen(false)}
          confirm={() => history.goBack()}
        />
        <ConfirmDialog
          isOpen={isClearConfirmDialogOpen}
          label="Wyczyszczenie formularza"
          close={() => setIsClearConfirmDialogOpen(false)}
          confirm={() => {
            reset();
            setIsClearConfirmDialogOpen(false);
          }}
        />
      </Content>
    </article>
  );
}
